import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";
import { queryClient } from "../lib/query-client";

interface User {
  id:      string;
  email:   string;
  name:    string;
  segment: string;
}

interface AuthState {
  token:   string | null;
  user:    User | null;
  // False until the persisted store has been read from localStorage. zustand's
  // persist middleware rehydrates asynchronously (a microtask after module
  // load) even for synchronous storage — reading `token` before this flips
  // true can render a one-frame "logged out" flash on a hard reload. Route
  // guards (App.tsx) hold a loading state until this is true.
  hasHydrated: boolean;
  setAuth: (token: string, user: User) => void;
  logout:  () => void;
}

// Root-caused hardening/auth-session defect: a rehydrated token was trusted
// without checking expiry (jwt.sign uses expiresIn: "24h" — see
// packages/api/src/plugins/auth.ts), and nothing reacted to the resulting
// stream of 401s. That 401 traffic exhausted the API's global, IP-keyed rate
// limit bucket (no trustProxy behind nginx — every candidate shares one
// bucket), which made GET /auth/config return 429, which
// GoogleOAuthButton.tsx treats identically to "Google is disabled" — so the
// button silently vanished. Fixed on both ends: here, a stale/corrupt token
// is detected and reset immediately on load (so it never starts generating
// 401 traffic), plus a scoped 401 interceptor resets it the moment the API
// itself rejects it mid-session; separately, GET /auth/config is now exempt
// from the rate limiter (packages/api/src/routes/auth.ts) so a login method
// can never appear "disabled" purely from being throttled.
//
// Decode a JWT payload WITHOUT verifying its signature — this is a client-side
// UX check only ("is it even worth sending?"), never a security boundary; the
// API is the only party that verifies the signature, on every request.
function isUsableJwt(token: unknown): boolean {
  if (typeof token !== "string") return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  try {
    const payloadJson = atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson) as { exp?: unknown };
    return typeof payload.exp === "number" && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token:   null,
      user:    null,
      hasHydrated: false,
      setAuth: (token, user) => {
        // A different person signing in on this tab must never be served the
        // previous account's cached data (see lib/query-client.ts).
        const previous = useAuthStore.getState().user;
        if (previous && previous.id !== user.id) queryClient.clear();
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        set({ token, user });
      },
      // Removes ONLY this store's own localStorage key — never a blanket
      // localStorage.clear(). Unrelated UI preferences (theme, the
      // seen-match-count watermark the nav badge uses, any future keys) are
      // untouched by design; see the product requirement this file's header
      // comment and the `partialize` option below both exist to satisfy.
      logout: () => {
        delete axios.defaults.headers.common["Authorization"];
        localStorage.removeItem("jobreel-auth");
        // Everything cached belongs to the account that just left.
        queryClient.clear();
        set({ token: null, user: null });
      },
    }),
    {
      name: "jobreel-auth",
      // Persist only the two fields that are meaningful across a reload.
      // `hasHydrated` is transient per-load UI state; persisting it was
      // harmless (onRehydrateStorage always re-derives it) but meant every
      // hydration wrote a stale value back to storage for no reason, and
      // widened what a future field accidentally persisted by default.
      partialize: (state) => ({ token: state.token, user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (state?.token && !isUsableJwt(state.token)) {
          // Expired or corrupt token rehydrated from a previous session.
          // Reset via the SAME logout() the explicit sign-out button uses —
          // removes only "jobreel-auth", nothing else — rather than letting
          // the app render as "logged in" and start generating 401 traffic.
          queueMicrotask(() => {
            useAuthStore.getState().logout();
            useAuthStore.setState({ hasHydrated: true });
          });
          return;
        }
        if (state?.token) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${state.token}`;
        }
        // In Zustand 5's persist middleware, calling setState synchronously inside
        // onRehydrateStorage can be swallowed/reset while persist completes its
        // synchronous hydration frame. Deferring the update to queueMicrotask
        // ensures hasHydrated reliably flips true on microtask completion for all
        // cases: clean browsers, empty storage, logged out, and authed sessions.
        queueMicrotask(() => {
          useAuthStore.setState({ hasHydrated: true });
        });
      },
    }
  )
);

// Mid-session expiry: the token was usable at load time but the API rejects
// it later (naturally expiring, revoked, etc.). Scoped narrowly so it can
// never fire on an unrelated 401 — only a request that actually CARRIED our
// Authorization header, while we still believe we're logged in. A
// wrong-password response from /auth/login carries no Authorization header
// and never reaches this branch.
axios.interceptors.response.use(undefined, (error) => {
  const sentAuthHeader = Boolean(
    (error?.config?.headers as Record<string, unknown> | undefined)?.Authorization
  );
  if (error?.response?.status === 401 && sentAuthHeader && useAuthStore.getState().token) {
    useAuthStore.getState().logout();
  }
  return Promise.reject(error);
});
